/* eslint-disable @typescript-eslint/no-unused-vars */
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useContext, useEffect, useState } from 'react';
import { Flex } from 'rebass/styled-components';

import { corewellHeroOverrideClasses, layout } from '../../../../constants/constants';
import { UiStateContext } from '../../../../shared/contexts/ui-state/UiStateContext';
import { Media } from '../../../contexts/media/MediaContext';
import useReferenceUrl from '../../../hooks/reference-url/useReferenceUrl';
import { HighImpactContainerHeroContent, IconPack } from '../../../models/app-data-model';
import ButtonWrapper from '../../button-wrapper/button-wrapper';
import ShIcon from '../../sh-icon/sh-icon';
import Video from '../../video/video';
import './high-impact-full-bleed-hero.scss';

export type HeroContentProps = {
  content: HighImpactContainerHeroContent;
  iconPack: IconPack[];
  subsiteNavPresent: boolean;
  headerPresent?: boolean;
  corewellStyleOverrides?: boolean;
};

const HighImpactFullBleedHero: React.FC<HeroContentProps> = ({ content, iconPack, subsiteNavPresent, headerPresent, corewellStyleOverrides }: HeroContentProps) => {
  const uiStateContext = useContext(UiStateContext);
  const desktopVideoUrl = useReferenceUrl(content.video_desktop?.video_link);
  const mobileVideoUrl = useReferenceUrl(content.video_mobile?.video_link);
  const noMarginTopClass = subsiteNavPresent || !headerPresent ? 'no-margin-top' : '';

  const [prefersReducedMotion, setPrefersReduceMotion] = useState(false);
  const [scrollToElement, setScrollToElement] = useState<HTMLDivElement>();

  useEffect(() => {
    if (typeof document !== 'undefined' && document.getElementById('high-impact-container')) {
      setScrollToElement(document.getElementById('scroll-to') as HTMLDivElement);
    }
  }, []);

  useEffect(() => {
    setPrefersReduceMotion(typeof window !== 'undefined' && window.matchMedia(`(prefers-reduced-motion)`).matches);
  }, []);

  const posterImageDesktop = (
    <GatsbyImage
      className={`hero-image-desktop ${desktopVideoUrl && !prefersReducedMotion ? 'video-placeholder' : ''}`}
      alt={content.image_desktop?.alt_text}
      image={content?.image_desktop?.file?.localAsset?.childImageSharp?.gatsbyImageData as IGatsbyImageData}
    />
  );

  const posterImageMobile = (
    <GatsbyImage
      className={`hero-image-mobile ${mobileVideoUrl && !prefersReducedMotion ? 'video-placeholder' : ''}`}
      alt={content.image_mobile?.alt_text}
      image={content?.image_mobile?.file?.localAsset?.childImageSharp?.gatsbyImageData as IGatsbyImageData}
    />
  );

  const lightFontMode = content.overlay_text_color?.color === 'light';
  const textColorClass = lightFontMode ? 'white' : '';

  const overlayText = (
    <Flex className="text-overlay" width={layout.pageWidth} px={layout.pagePaddingX} margin={'auto'}>
      <div className="heading-container">
        <h1 className={`${textColorClass} ${content.hide_header ? 'hidden' : ''}`}>{content.header}</h1>
        {content.subtext && !content.hide_header && <h2 className={textColorClass}>{content.subtext}</h2>}
      </div>
    </Flex>
  );

  const overlayShading = !content.hide_header && (
    <div className={`overlay-shading ${content.overlay_shading?.color}`} style={{ opacity: `${content.overlay_shading_opacity}%` }}></div>
  );

  const scrolldownBtnClasses = `scroll-down-btn ${lightFontMode ? '' : 'dark'} ${uiStateContext.pageHasScrolled ? 'fade-out' : 'fade-in'}`;

  const marginTopOverride = corewellStyleOverrides ? `${corewellHeroOverrideClasses?.heroMarginTopOverride}` : '';
  const highImpactContainerClasses = `high-impact-full-bleed-hero ${noMarginTopClass} ${marginTopOverride}`;

  return (
    <div className={highImpactContainerClasses}>
      <div className="hero-container" id="high-impact-container">
        <Media greaterThanOrEqual="tablet">
          {posterImageDesktop}
          {desktopVideoUrl && !prefersReducedMotion && (
            <Video
              className="hero-video"
              width="100vw"
              height={`${900 / 16}vw`}
              isBackground={true}
              controls={false}
              playing={true}
              poster={posterImageDesktop}
              url={desktopVideoUrl.href}
              volume={0}
              muted={true}
              autoplay={true}
              loop={true}
            ></Video>
          )}
          {overlayShading}
          {overlayText}
        </Media>
        <Media lessThan="tablet">
          {posterImageMobile}
          {mobileVideoUrl && !prefersReducedMotion && (
            <Video
              className="hero-video"
              width="100vw"
              height={`${1600 / 9}vw`}
              isBackground={true}
              controls={false}
              playing={true}
              poster={posterImageMobile}
              url={mobileVideoUrl.href}
              volume={0}
              muted={true}
              autoplay={true}
              loop={true}
            ></Video>
          )}
          {overlayShading}
          {overlayText}
        </Media>
        {content?.call_to_action_button?.text && content?.call_to_action_button?.reference ? (
          <div className="cta-btn-container">
            <ButtonWrapper
              type={content?.call_to_action_button?.button_type ? content?.call_to_action_button?.button_type : 'primary'}
              styleOverride={'wide-button'}
              label={content?.call_to_action_button?.text}
              reference={content?.call_to_action_button?.reference}
              iconPack={iconPack}
            />
          </div>
        ) : (
          <button
            onClick={() => scrollToElement?.scrollIntoView({ behavior: 'smooth' })}
            className={`unstyled-btn ${scrolldownBtnClasses}`}
            id="scroll-down-btn"
            data-testid="scroll-down-btn"
          >
            <ShIcon iconPack={iconPack} iconName={lightFontMode ? 'white_arrow' : 'arrow'} className="scroll-down-icon" />
          </button>
        )}
      </div>
      <div id="scroll-to" className="scroll-to"></div>
    </div>
  );
};

export default HighImpactFullBleedHero;
